import { useState, useEffect, useCallback } from "react";
import { Modal, Button, Pagination, Toast, useToast } from "@hiya/ui";
import {
  listPushNotifications,
  createPushNotification,
  updatePushNotification,
  deletePushNotification,
} from "../../../api/notifications";
import type { PushNotification } from "../../../types/notification";
import { KebabMenu } from "../../../components/KebabMenu";
import iconPlus from "../../../assets/icon-plus.svg";
import closeSvg from "../../../assets/icon-close.svg";

const PAGE_SIZE = 10;

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${month}.${year}; ${hours}:${minutes}`;
}

/**
 * Format an ISO timestamp as a local-time string usable by
 * <input type="datetime-local">: YYYY-MM-DDTHH:mm
 */
function formatDateForInput(iso: string): string {
  const d = new Date(iso);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Parse the value produced by <input type="datetime-local"> (YYYY-MM-DDTHH:mm)
 * and return an ISO UTC string, or null if the value is not a valid date.
 */
function parseDateInput(value: string): string | null {
  const pattern = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;
  const match = pattern.exec(value);
  if (!match) return null;
  const [, year, month, day, hours, minutes] = match;
  const d = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
  );
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Current local time formatted for the `min` attribute of
 * <input type="datetime-local"> — prevents scheduling in the past.
 */
function localNowForInput(): string {
  return formatDateForInput(new Date().toISOString());
}

// ── Side Panel ──────────────────────────────────────────────────────

interface SidePanelProps {
  open: boolean;
  title: string;
  heading: string;
  text: string;
  sendTime: string;
  saving: boolean;
  onHeadingChange: (v: string) => void;
  onTextChange: (v: string) => void;
  onSendTimeChange: (v: string) => void;
  onSave: () => void;
  onClose: () => void;
}

function SidePanel({
  open,
  title,
  heading,
  text,
  sendTime,
  saving,
  onHeadingChange,
  onTextChange,
  onSendTimeChange,
  onSave,
  onClose,
}: Readonly<SidePanelProps>) {
  if (!open) return null;

  const canSave =
    heading.trim().length > 0 &&
    text.trim().length > 0 &&
    sendTime.trim().length > 0 &&
    parseDateInput(sendTime) !== null &&
    !saving;

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-overlay border-none cursor-default"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        tabIndex={-1}
        aria-label="Close panel"
      />
      <div className="relative w-[480px] h-full bg-bg-primary shadow-lg flex flex-col">
        <div className="flex flex-col gap-5 p-5 flex-1 overflow-y-auto">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-text-primary">{title}</h2>
            <button onClick={onClose} aria-label="Close">
              <img src={closeSvg} alt="" width={24} height={24} />
            </button>
          </div>

          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={heading}
              onChange={(e) => onHeadingChange(e.target.value)}
              placeholder="Heading"
              className="h-[51px] rounded-2xl border border-stroke-primary px-4 text-base text-text-primary bg-bg-primary placeholder:text-text-tertiary focus:outline-none focus:border-brand-brown"
            />
            <textarea
              value={text}
              onChange={(e) => onTextChange(e.target.value)}
              placeholder="Text"
              className="min-h-[80px] rounded-2xl border border-stroke-primary p-4 text-base text-text-primary bg-bg-primary resize-none placeholder:text-text-tertiary focus:outline-none focus:border-brand-brown"
            />
            <input
              type="datetime-local"
              value={sendTime}
              onChange={(e) => onSendTimeChange(e.target.value)}
              min={localNowForInput()}
              className="h-[51px] w-full rounded-2xl border border-stroke-primary px-4 text-base text-text-primary bg-bg-primary placeholder:text-text-tertiary focus:outline-none focus:border-brand-brown"
            />
          </div>
        </div>

        <div className="px-5 pb-5">
          <button
            onClick={onSave}
            disabled={!canSave}
            className={`w-full h-[51px] rounded-2xl font-semibold text-base text-text-btn transition-colors ${
              canSave
                ? "bg-btn-primary cursor-pointer"
                : "bg-btn-primary-disabled cursor-not-allowed"
            }`}
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Status Badge ────────────────────────────────────────────────────

interface StatusBadgeProps {
  status: PushNotification["delivery_status"];
  sendAt: string;
}

function StatusBadge({ status, sendAt }: Readonly<StatusBadgeProps>) {
  const dateStr = formatDate(sendAt);

  if (status === "sent") {
    return (
      <span className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-[rgba(118,151,124,0.08)] text-sm text-[#76977c] whitespace-nowrap">
        Sent {dateStr}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center justify-center px-3 py-1.5 rounded-full bg-brand-brown-6 text-sm text-brand-brown whitespace-nowrap">
      Will be sent {dateStr}
    </span>
  );
}

// ── Main Page ───────────────────────────────────────────────────────

export function PushNotificationsPage() {
  const [items, setItems] = useState<PushNotification[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<PushNotification | null>(null);
  const [editTarget, setEditTarget] = useState<PushNotification | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelMode, setPanelMode] = useState<"create" | "edit">("create");
  const [heading, setHeading] = useState("");
  const [text, setText] = useState("");
  const [sendTime, setSendTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { toast, showToast } = useToast();

  const loadItems = useCallback(
    async (p: number) => {
      try {
        setLoading(true);
        const data = await listPushNotifications(p, PAGE_SIZE);
        setItems(data.items);
        setTotal(data.total);
      } catch {
        showToast("Failed to load push notifications", "warning");
      } finally {
        setLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    loadItems(page);
  }, [loadItems, page]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function openCreatePanel() {
    setPanelMode("create");
    setEditTarget(null);
    setHeading("");
    setText("");
    setSendTime("");
    setPanelOpen(true);
  }

  function openEditPanel(item: PushNotification) {
    setPanelMode("edit");
    setEditTarget(item);
    setHeading(item.heading);
    setText(item.text);
    setSendTime(formatDateForInput(item.send_at));
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditTarget(null);
    setHeading("");
    setText("");
    setSendTime("");
  }

  async function handleSave() {
    if (saving) return;
    const sendAtIso = parseDateInput(sendTime);
    if (!sendAtIso) return;

    setSaving(true);
    try {
      if (panelMode === "create") {
        const created = await createPushNotification({
          heading: heading.trim(),
          text: text.trim(),
          send_at: sendAtIso,
        });
        setItems((prev) => [created, ...prev]);
        setTotal((prev) => prev + 1);
        showToast("New Push Notification added");
      } else if (editTarget) {
        const updated = await updatePushNotification(editTarget.id, {
          heading: heading.trim(),
          text: text.trim(),
          send_at: sendAtIso,
        });
        setItems((prev) =>
          prev.map((it) => (it.id === updated.id ? updated : it)),
        );
        showToast("Push Notification edited");
      }
      closePanel();
    } catch {
      showToast(
        panelMode === "create"
          ? "Failed to create push notification"
          : "Failed to update push notification",
        "warning",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deletePushNotification(deleteTarget.id);
      setItems((prev) => prev.filter((it) => it.id !== deleteTarget.id));
      setTotal((prev) => Math.max(0, prev - 1));
      showToast("Push Notification deleted");
    } catch {
      showToast("Failed to delete push notification", "warning");
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  }

  return (
    <div className="overflow-x-hidden">
      <Toast message={toast.message} visible={toast.visible} variant={toast.variant} />

      {/* New Push Notification button */}
      <button
        type="button"
        onClick={openCreatePanel}
        className="w-full h-[51px] mb-5 rounded-2xl bg-brand-brown-6 text-brand-brown text-base font-semibold flex items-center justify-center gap-1"
      >
        <img src={iconPlus} alt="" width={24} height={24} />{" "}
        <span>New Push Notification</span>
      </button>

      {loading && (
        <div className="flex flex-col">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-[84px] bg-bg-secondary animate-pulse mb-4 rounded-xl"
            />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base">
            No push notifications yet
          </p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <>
          <div className="flex flex-col">
            {items.map((item, index) => (
              <div key={item.id}>
                <div className="flex flex-col gap-2">
                  <div className="flex gap-3 items-start">
                    <p className="flex-1 text-base font-semibold text-text-primary leading-[1.2]">
                      {item.heading}
                    </p>
                    <KebabMenu
                      onEdit={() => openEditPanel(item)}
                      onDelete={() => setDeleteTarget(item)}
                    />
                  </div>
                  <p className="text-base font-medium text-text-secondary leading-[1.2] line-clamp-1">
                    {item.text}
                  </p>
                  <div>
                    <StatusBadge
                      status={item.delivery_status}
                      sendAt={item.send_at}
                    />
                  </div>
                </div>
                {index < items.length - 1 && (
                  <div className="border-b border-stroke-primary my-5" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-5">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />
          </div>
        </>
      )}

      <SidePanel
        open={panelOpen}
        title={
          panelMode === "create"
            ? "New Push Notification"
            : "Edit Push Notification"
        }
        heading={heading}
        text={text}
        sendTime={sendTime}
        saving={saving}
        onHeadingChange={setHeading}
        onTextChange={setText}
        onSendTimeChange={setSendTime}
        onSave={handleSave}
        onClose={closePanel}
      />

      <Modal
        open={deleteTarget !== null}
        title="Delete Notification"
        onClose={() => setDeleteTarget(null)}
      >
        <p className="text-sm text-text-primary leading-[1.5]">
          Are you sure you want to delete notification on{" "}
          {deleteTarget ? formatDate(deleteTarget.send_at) : ""}? It will not be
          published for users
        </p>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete notification"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
